<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('super-organizations')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Create Organization</h1>
        </header>

        <section class="module__body">
            <form @submit.prevent="createOrganization">
                <div class="form-group">
                    <label for="name">Name</label>
                    <input
                        id="name"
                        v-model="name"
                        type="text"
                        required
                        maxlength="32"
                    />
                </div>

                <div class="form-group organization-type">
                    <label for="organization_type">Organization Type</label>
                    <select
                        id="organization-type"
                        v-model="organizationType"
                        data-test-id="organization-type"
                        required
                    >
                        <option
                            disabled
                            selected
                            value=""
                        >
                            Please Choose Type
                        </option>
                        <option
                            v-for="type in organizationTypes"
                            :key="type"
                            :value="type"
                        >
                            {{ type }}
                        </option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="admin_emails">
                        Admins
                        <span class="tip">(List of emails, each separated by new line)</span>
                    </label>
                    <textarea
                        id="admin_emails"
                        v-model="admin_emails"
                        required
                    />
                </div>

                <fieldset>
                    <legend>Limits</legend>

                    <div class="form-group">
                        <label for="frequency_set_limit">Frequency Set Limit</label>
                        <input
                            id="frequency_set_limit"
                            v-model="frequency_set_limit"
                            type="number"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="field_user_limit">User Limit</label>
                        <input
                            id="field_user_limit"
                            v-model="field_user_limit"
                            type="number"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="mission_control_user_limit">Map User Limit</label>
                        <input
                            id="mission_control_user_limit"
                            v-model="mission_control_user_limit"
                            type="number"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="pli_limit">PLI Limit</label>
                        <input
                            id="pli_limit"
                            v-model="pli_limit"
                            type="number"
                            required
                        />
                    </div>

                    <div class="form-group">
                        <label for="mapbox_tile_limit">Mapbox Tile Limit</label>
                        <input
                            id="mapbox_tile_limit"
                            v-model="mapbox_tile_limit"
                            type="number"
                            required
                        />
                    </div>
                </fieldset>

                <fieldset>
                    <legend>Portal Permissions</legend>
                    <feature-toggle-field
                        :availableFeatures="availableOrganizationFeatures"
                        :selectedFeatures="selectedOrganizationFeatures"
                        @toggle="organizationFeatureToggled"
                    ></feature-toggle-field>
                </fieldset>

                <fieldset>
                    <legend>Mobile Permissions + Limits</legend>
                    <feature-toggle-field
                        :availableFeatures="availableMobileFeatures"
                        :selectedFeatures="selectedMobileFeatures"
                        showLimits
                        @toggle="mobileFeatureToggled"
                        @limit-update="mobileFeatureLimitUpdated"
                    ></feature-toggle-field>
                </fieldset>

                <fieldset>
                    <legend>Salesforce</legend>
                    <orders-field
                        :orders="orders"
                        @add-order="orders.push($event)"
                        @remove-order="orders.splice($event, 1)"
                    ></orders-field>
                </fieldset>

                <fieldset>
                    <legend>Billing Cycle</legend>
                    <billing-cycle-field
                        :availableFeatures="availablePremiumMobileFeatures"
                        :selectedFeatures="selectedMobileFeatures"
                        :billingIsRecurring="billing_is_recurring"
                        :billingStartedAt="billing_started_at"
                        :billingRenewsOn="billing_renews_on"
                        :licenseOverageIsVisible="licenseOverageIsVisible"
                        :licenseOverageAmount="license_overage_amount"
                        :licenseOverageType="license_overage_type"
                        :licenseOverageIsLocked="licenseOverageIsLocked && !licenseOverageIsVisible"
                        @toggle="mobileFeatureToggled"
                        @limit-update="mobileFeatureLimitUpdated"
                        @recurring-update="billing_renews_on = (billing_is_recurring = $event) ? billing_renews_on : ''"
                        @start-date-update="billing_started_at = $event"
                        @renewal-date-update="billing_renews_on = $event"
                        @license-overage-is-visible-update="licenseOverageIsVisible = $event"
                        @license-overage-amount-update="license_overage_amount = $event"
                        @license-overage-type-update="license_overage_type = $event"
                    />
                </fieldset>

                <fieldset>
                    <legend>License Purchase History</legend>
                    <license-purchases-field
                        :purchases="licenses"
                        :usedCount="0"
                        :licenseOverageIsVisible="licenseOverageIsVisible"
                        @license-purchase-change="licensePurchasesChanged"
                    ></license-purchases-field>
                </fieldset>

                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :class="{ active: submitted }"
                    >
                        <div>Create</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import FormErrors from '@/components/FormErrors.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import FeatureToggleField from '@/components/FeatureToggleField.vue'
import OrdersField from '@/components/OrdersField.vue'
import BillingCycleField from '@/components/BillingCycleField.vue'
import LicensePurchasesField from '@/components/LicensePurchasesField.vue'
import organizationTypes from '@/constants/organization-types'

export default defineComponent({
    components: {
        FormErrors,
        FeatureToggleField,
        OrdersField,
        BillingCycleField,
        LicensePurchasesField,
    },

    mixins: [canToast, hasBackButton],

    data() {
        return {
            name: null,
            admin_emails: null,
            frequency_set_limit: 25,
            field_user_limit: 100,
            pli_limit: 5,
            mapbox_tile_limit: 20000,
            mission_control_user_limit: 1,
            errorResponse: null,
            submitted: false,
            orders: [],
            selectedOrganizationFeatures: {},
            availableOrganizationFeatures: [],
            selectedMobileFeatures: {},
            availableMobileFeatures: [],
            availablePremiumMobileFeatures: [],
            licenses: [],
            billing_is_recurring: false,
            billing_started_at: '',
            billing_renews_on: '',
            licenseOverageIsVisible: false,
            license_overage_amount: 20,
            license_overage_type: 'PERCENTAGE',
            organization_type: null,
            organizationTypes,
        }
    },

    computed: {
        licenseOverageIsLocked() {
            return !this.licenses.some(({ count }) => count !== null && count > 0)
        },

        organizationType: {
            get() {
                return this.organization_type === null ? '' : this.organization_type
            },

            set(value) {
                this.organization_type = Object.values(organizationTypes).includes(value) ? value : null
            },
        },
    },

    mounted() {
        this.$http.get('organization-features').then(({ data }) => {
            this.availableOrganizationFeatures = data
        })

        this.$http.get('mobile-features/all').then(({ data }) => {
            this.availableMobileFeatures = data.filter((feature) => !feature.is_premium)
            this.availablePremiumMobileFeatures = data.filter((feature) => feature.is_premium)
        })
    },

    methods: {
        organizationFeatureToggled(feature) {
            if (this.selectedOrganizationFeatures[feature.id]) {
                delete this.selectedOrganizationFeatures[feature.id]
            } else {
                this.selectedOrganizationFeatures[feature.id] = {
                    id: feature.id,
                    limit: null,
                }
            }
        },

        mobileFeatureToggled(feature) {
            if (this.selectedMobileFeatures[feature.id]) {
                delete this.selectedMobileFeatures[feature.id]
            } else {
                this.selectedMobileFeatures[feature.id] = {
                    id: feature.id,
                    limit: null,
                }
            }
        },

        mobileFeatureLimitUpdated(feature, limit) {
            if (this.selectedMobileFeatures[feature.id]) {
                this.selectedMobileFeatures[feature.id].limit = limit
            }
        },

        billingCycleRecurringUpdated(value) {
            this.billingIsRecurring = value
        },

        licensePurchasesChanged(licenses) {
            this.licenses = licenses
        },

        createOrganization() {
            this.submitted = true

            const features = Object.values(this.selectedOrganizationFeatures).map((selectedFeature) => {
                return this.availableOrganizationFeatures.find((feature) => feature.id === selectedFeature.id)
            })

            const mobileFeatures = Object.values(this.selectedMobileFeatures)

            let licenseOverageAmount = this.license_overage_amount
            let licenseOverageType = this.license_overage_type

            // if overage section is not visible, or the value is zero, set
            // overage info to null
            if (!this.licenseOverageIsVisible || licenseOverageAmount === 0) {
                licenseOverageAmount = null
                licenseOverageType = null
            }

            this.$http
                .post('organizations', {
                    name: this.name,
                    admin_emails: this.admin_emails,
                    frequency_set_limit: this.frequency_set_limit,
                    field_user_limit: this.field_user_limit,
                    pli_limit: this.pli_limit,
                    mapbox_tile_limit: this.mapbox_tile_limit,
                    mission_control_user_limit: this.mission_control_user_limit,
                    orders: this.orders,
                    mobile_features: mobileFeatures,
                    features,
                    licenses: this.licenses,
                    billing_is_recurring: Number(this.billing_is_recurring),
                    billing_started_at: this.billing_started_at,
                    billing_renews_on: this.billing_renews_on,
                    license_overage_amount: licenseOverageAmount,
                    license_overage_type: licenseOverageType,
                    type: this.organization_type,
                })
                .then(() => {
                    this.toast('Organization successfully created!')
                    this.$router.push('/organizations')
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },
    },
})
</script>
